/**
 * Only a project-type product or service can take downtime. Mirror of the server's
 * `schemas/time_tracking.py::check_downtime_product`, which refuses the save
 * with 422 whatever this screen offers -- these helpers only keep the screen
 * from offering what the server will refuse.
 *
 * Kept pure so vitest (node, no jsdom) can pin them.
 *
 * ⚠️ UNKNOWN IS NOT NON-PROJECT. The product list the screen holds is the
 * active, non-Subteam list, so a line on a retired product has no known
 * `type_of`. The CONTROLS fail closed on that (downtime not offered unless the
 * product is a KNOWN Project); the error SENTENCE says nothing unless the
 * product is known to be off a Project or is missing (CLAUDE.md §6(b)).
 */
import { PROJECT_TYPE_OF } from '../../lib/typicalShift'

/** `product id -> type_of`, for the products the screen has loaded. */
export type ProductTypes = ReadonlyMap<string, string>

export function productTypesOf(
  products: readonly { id: string; type_of: string }[] | undefined,
): ProductTypes {
  return new Map((products ?? []).map((p) => [p.id, p.type_of]))
}

/** true / false when the product's type is known; undefined when it is not. */
export function isProjectProduct(
  productId: string | null | undefined,
  types: ProductTypes,
): boolean | undefined {
  if (!productId) return false
  const typeOf = types.get(productId)
  return typeOf === undefined ? undefined : typeOf === PROJECT_TYPE_OF
}

/**
 * The activities a line may pick: a downtime activity only while the line's
 * product is a KNOWN Project. `keep` (the line's current activity) always
 * stays, or opening an old line would silently rewrite it.
 */
export function activitiesForProduct<T extends { id: string; is_downtime: boolean }>(
  activities: readonly T[],
  productId: string | null | undefined,
  types: ProductTypes,
  keep?: string,
): T[] {
  if (isProjectProduct(productId, types) === true) return [...activities]
  return activities.filter((a) => !a.is_downtime || a.id === keep)
}

/**
 * The products a line may pick: on a downtime line, Project ones only.
 * `keep` (the line's current product) always stays.
 */
export function productsForActivity<T extends { id: string; type_of: string }>(
  products: readonly T[],
  activityIsDowntime: boolean,
  keep?: string | null,
): T[] {
  if (!activityIsDowntime) return [...products]
  return products.filter((p) => p.type_of === PROJECT_TYPE_OF || p.id === keep)
}

/**
 * Whether any downtime line sits on a product KNOWN to be off a Project, or on
 * no product at all. A product whose type is unknown does not count -- the
 * server is the backstop for that case.
 */
export function downtimeOffProject(
  rows: readonly { activity_id: string; product_service_id: string | null }[],
  downtimeIds: ReadonlySet<string>,
  types: ProductTypes,
): boolean {
  return rows.some(
    (r) =>
      downtimeIds.has(r.activity_id) && isProjectProduct(r.product_service_id, types) === false,
  )
}
